import { useMemo } from 'react'
import Slider, { Settings } from 'react-slick'
import DPFMemoryImg1 from '../assets/landing/DPF-memory-img-1.png'
import { CuratedPhotoForPhotoProducts } from '../types'

const SliderComponent = Slider as unknown as React.ComponentClass<Settings>

function MemoriesCarousel({
  slides,
  isUsingFallback,
}: {
  slides: Array<[string, string]>
  isUsingFallback: boolean
}) {
  const carouselSettings: Settings = useMemo(
    () => ({
      dots: true,
      infinite: true,
      slidesToShow: 1,
      slidesToScroll: 1,
      autoplay: true,
      speed: 1200,
      autoplaySpeed: 3000,
      cssEase: 'ease',
      arrows: false,
      swipeToSlide: true,
      appendDots: (dots: JSX.Element) => (
        <div className='absolute bottom-4 left-0 right-0 z-10'>
          <ul className='flex justify-center gap-2 m-0 p-0 list-none'>{dots}</ul>
        </div>
      ),
      customPaging: () => (
        <div className='w-2 h-2 rounded-full bg-gray-300 transition-all duration-200' />
      ),
    }),
    [],
  )

  const containerClasses = 'relative rounded-lg overflow-hidden h-[218px] sm:h-[260px]'
  const sliderClassName = 'memories-carousel'

  // Fallback to single static image if using fallback or no slides
  if (isUsingFallback || slides.length === 0) {
    return (
      <div className={containerClasses}>
        <img src={DPFMemoryImg1} alt='' className='w-full h-full object-cover' loading='eager' />
      </div>
    )
  }

  return (
    <div className={containerClasses}>
      <style>{`
        .${sliderClassName} {
          position: relative;
          height: 100%;
        }
        .${sliderClassName} .slick-list,
        .${sliderClassName} .slick-track,
        .${sliderClassName} .slick-slide,
        .${sliderClassName} .slick-slide > div {
          height: 100%;
        }
        .${sliderClassName} .slick-dots {
          position: absolute !important;
          bottom: 16px !important;
          left: 0 !important;
          right: 0 !important;
          z-index: 10 !important;
        }
      `}</style>
      <SliderComponent className={sliderClassName} {...carouselSettings}>
        {slides.map((slide, slideIdx) => (
          <div key={slideIdx} className='px-0.5 h-full'>
            <div className='grid grid-cols-2 gap-0.5 h-full'>
              <img
                src={slide[0]}
                alt=''
                className='w-full h-full object-cover rounded-tl-lg rounded-bl-lg'
                loading={slideIdx === 0 ? 'eager' : 'lazy'}
              />
              <img
                src={slide[1]}
                alt=''
                className='w-full h-full object-cover rounded-tr-lg rounded-br-lg'
                loading={slideIdx === 0 ? 'eager' : 'lazy'}
              />
            </div>
          </div>
        ))}
      </SliderComponent>
    </div>
  )
}

export default function MemoriesSection({
  curatedPhotos,
  isUsingFallback,
}: {
  curatedPhotos: CuratedPhotoForPhotoProducts[]
  isUsingFallback: boolean
}) {
  // Create slides with 2 photos per slide
  const slides = useMemo<Array<[string, string]>>(() => {
    if (isUsingFallback || curatedPhotos.length < 6) {
      return []
    }
    const photoUrls = curatedPhotos.slice(0, 6).map(photo => photo.croppedUrl)
    return [
      [photoUrls[0], photoUrls[1]],
      [photoUrls[2], photoUrls[3]],
      [photoUrls[4], photoUrls[5]],
    ]
  }, [curatedPhotos, isUsingFallback])

  const bodyText = useMemo(
    () => [
      'みてねをご利用いただいているご家庭で、3歳以上のお子さまがいる場合、平均で7,000件を超える写真や動画が共有されています。',
      'しかし、そのうち実際に見返されているのは、わずか5%ほどです。たくさんの思い出が、みてねの中でそっと眠ったままになっています。',
      'このフォトフレームは、忙しい毎日のなかでもたくさんの思い出を振り返られるように、そんな想いから生まれました。',
    ],
    [],
  )

  return (
    <section className='px-4 py-10 bg-[#FEFBE9]'>
      <div className='space-y-6'>
        <div className='text-center'>
          <h2 className='text-2xl font-semibold text-gray-12 leading-[140%] tracking-0'>
            昔の思い出、
            <br />
            埋もれてしまってませんか?
          </h2>
        </div>
        <MemoriesCarousel slides={slides} isUsingFallback={isUsingFallback} />
        <div className='text-base font-normal text-gray-12 leading-[160%] tracking-0 text-left space-y-0'>
          {bodyText.map((paragraph, idx) => (
            <p key={idx}>{paragraph}</p>
          ))}
        </div>
      </div>
    </section>
  )
}
